import React, { useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { closeModal, closeModalWithUpdate } from "../../../features/modal/ModalSlice";
import { Box, Chip, TextField, Typography, Divider, Paper } from "@mui/material";
import CustomButton from "../../Button/CustomButton";
import apiClient from "../../../services/apiClient";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { MdEmail } from "react-icons/md";

const AddMember = () => {
  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state?.userInfo?.user);
  const modal = useSelector((state) => state?.modal?.modal);

  // Get source from modal data (team-members or invites)
  const source = modal?.data?.source || "team-members";

  const handleKeyDown = (event) => {
    if (
      (event.key === " " || event.key === "Enter" || event.key === ",") &&
      inputValue.trim() !== ""
    ) {
      event.preventDefault();
      setEmails([...emails, inputValue.trim()]);
      setInputValue("");
      setError(""); // Clear error when adding email
    }
  };

  const handleDelete = (emailToDelete) => () => {
    setEmails(emails.filter((email) => email !== emailToDelete));
  };

  const handleInvitePage = async () => {
    // incase of one email
    let extraEmail = inputValue.trim();
    if (inputValue.trim() !== "") {
      setEmails((prev) => [...prev, inputValue.trim()]);
      setInputValue("");
    }

    // validation for emails
    if (emails.length === 0 && inputValue.trim() === "") {
      setError("Please enter at least one email.");
      return;
    }

    setLoading(true);
    try {
      await apiClient.post("/invite", {
        invited_by: userInfo?.id,
        emails: extraEmail ? [...emails, extraEmail] : emails,
        company_id: userInfo?.company_id,
        invite_status: "Pending",
        stage_no: "1",
      });

      toast.success(source === "team-members" ? "Team member invited successfully!" : "Invite sent successfully!");
      dispatch(
        closeModalWithUpdate({
          isUpdated: {
            isUpdatedIn: "TEAMS",
            data: null,
          },
        })
      );

      // Navigate based on source
      if (source === "invites") {
        navigate("/invites");
      } else {
        navigate("/users");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const { already_invited_emails, message } = error.response.data;
        let errorMessage = message || "Try again";
        if (already_invited_emails && already_invited_emails.length > 0) {
          errorMessage += `: ${already_invited_emails.join(", ")}`;
        }
        setError(errorMessage);
      } else {
        setError("Try again");
      }
      console.log(error);
      toast.error("Oops! Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleClosePopup = () => {
    dispatch(closeModal());
  };

  return (
    <div className="rightModal taskModal">
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <MdEmail style={{ color: "white", fontSize: 24 }} />
          <Typography variant="h5" sx={{ color: "white", fontWeight: 600, m: 0 }}>
            {source === "team-members" ? "Add Team Member" : "Send New Invite"}
          </Typography>
        </Box>
        <button
          className="btnClose"
          onClick={handleClosePopup}
          style={{
            background: "rgba(255, 255, 255, 0.2)",
            border: "none",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          }}
        >
          <IoCloseOutline style={{ color: "white", fontSize: 20 }} />
        </button>
      </Box>

      <Divider />

      {/* Body */}
      <Box sx={{ p: 3 }}>
        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          {source === "team-members"
            ? "Add new team members to your workspace by entering their email addresses. Press Space, Enter, or Comma to add each email."
            : "Send invitations to new members by entering their email addresses. Press Space, Enter, or Comma to add each email."
          }
        </Typography>

        {/* Email Chips Display */}
        {emails.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              minHeight: "60px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#6B7280", fontWeight: 600, mb: 1, display: "block" }}
            >
              Added Emails ({emails.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {emails.map((email, index) => (
                <Chip
                  key={index}
                  label={email}
                  onDelete={handleDelete(email)}
                  size="small"
                  sx={{
                    backgroundColor: "#667eea",
                    color: "white",
                    fontWeight: 500,
                    fontSize: "0.813rem",
                    "& .MuiChip-deleteIcon": {
                      color: "rgba(255, 255, 255, 0.7)",
                      "&:hover": {
                        color: "white",
                      },
                    },
                  }}
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* Email Input Field */}
        <TextField
          label="Email Addresses"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={handleKeyDown}
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          placeholder="Type email and press Space, Enter, or Comma to add"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          error={!!error}
          helperText={
            error || (
              <span>
                <strong>Tip:</strong> You can paste multiple emails separated by spaces or commas
              </span>
            )
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "white",
            },
          }}
        />

        {/* Info Box */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: "#F0F9FF",
            borderRadius: "8px",
            border: "1px solid #BAE6FD",
            display: "flex",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "#0EA5E9",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              fontWeight: "bold",
              flexShrink: 0,
            }}
          >
            i
          </Box>
          <Typography variant="body2" sx={{ color: "#0369A1", fontSize: "0.875rem", lineHeight: 1.6 }}>
            {source === "team-members"
              ? "New team members will receive an invitation email with instructions to join your workspace. They can set up their profile and extensions after accepting the invitation."
              : "Invited members will receive an email with a link to accept the invitation. Once accepted, they will appear in the Team Members section."
            }
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Footer */}
      <Box className="d-flex rightModalFooterBtn" sx={{ borderTop: "1px solid #E5E7EB" }}>
        <CustomButton
          type="button"
          variant="contained"
          onClick={handleClosePopup}
          disabled={loading}
          sx={{
            minWidth: "40%",
            borderRadius: "0px",
            backgroundColor: "#FFFFFF",
            color: "#333",
            padding: "14px 16px",
            fontWeight: 500,
            fontSize: "0.938rem",
            boxShadow: "none",
            borderRight: "1px solid #E5E7EB",
            "&:hover": {
              backgroundColor: "#F9FAFB",
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: "#F3F4F6",
              color: "#9CA3AF",
            },
          }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          type="submit"
          variant="contained"
          onClick={handleInvitePage}
          loading={loading}
          disabled={emails.length === 0 && inputValue.trim() === ""}
          sx={{
            minWidth: "60%",
            borderRadius: "0px",
            backgroundColor: "#667eea",
            color: "#FFFFFF",
            padding: "14px 16px",
            fontWeight: 600,
            fontSize: "0.938rem",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#5568d3",
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {source === "team-members"
            ? `Add ${emails.length > 0 ? `(${emails.length}) ` : ""}Member${emails.length !== 1 ? "s" : ""}`
            : `Send ${emails.length > 0 ? `(${emails.length}) ` : ""}Invitation${emails.length !== 1 ? "s" : ""}`
          }
        </CustomButton>
      </Box>
    </div>
  );
};

export default AddMember;
